import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import toast from 'react-hot-toast';
import { daznService, DaznAccount as DaznAccountType, DaznTokenStatus, DaznDevice, DaznSubscriptionDetail, proxyService, Proxy, getErrorMessage } from '@/services';
import { DAZN_COUNTRIES, DAZN_LANGUAGES, TIMEZONES } from '@/utils/dazn-constants';
import { identityPayload } from '@/utils/dazn-account-form';
import { withSubscriptionData } from '@/utils/subscriptions';
import WvdCard from '@/components/dazn/WvdCard';

const daznSchema = z.object({
  dazn_email: z.string().email('validation.email'),
  dazn_password: z.string().optional(),
  dazn_device_id: z.string().optional(),
  dazn_country: z.string().min(2),
  dazn_language: z.string().min(2),
  dazn_timezone: z.string().min(1),
  dazn_pin: z.string().max(4).optional(),
  dazn_user_agent: z.string().optional(),
  dazn_x_dazn_ua: z.string().optional(),
  proxy_id: z.string().optional(),
});

type DaznForm = z.infer<typeof daznSchema>;

// Icone SVG inline
const Icons = {
  User: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
    </svg>
  ),
  Mail: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  Globe: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
    </svg>
  ),
  Device: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  Clock: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  ),
  Shield: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
    </svg>
  ),
  Edit: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
    </svg>
  ),
  Check: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
    </svg>
  ),
  X: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
    </svg>
  ),
  Warning: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
    </svg>
  ),
  Zap: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
    </svg>
  ),
  Key: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 7a2 2 0 012 2m4 0a6 6 0 01-7.743 5.743L11 17H9v2H7v2H4a1 1 0 01-1-1v-2.586a1 1 0 01.293-.707l5.964-5.964A6 6 0 1121 9z" />
    </svg>
  ),
  Crown: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 3l4 4 3-3 3 3 4-4v14H5V3z" />
    </svg>
  ),
  Logout: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
    </svg>
  ),
  Star: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
    </svg>
  ),
  Eye: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
    </svg>
  ),
  EyeOff: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
    </svg>
  ),
  Refresh: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
    </svg>
  ),
  Trash: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
  ),
  CreditCard: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
    </svg>
  ),
  Calendar: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
    </svg>
  ),
  Monitor: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
    </svg>
  ),
  MapPin: () => (
    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
    </svg>
  ),
};

export default function DaznAccount() {
  const { t } = useTranslation();
  const [account, setAccount] = useState<DaznAccountType | null>(null);
  const [hasDefaultProxy, setHasDefaultProxy] = useState(false);
  const [proxies, setProxies] = useState<Proxy[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isTesting, setIsTesting] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [tokenStatus, setTokenStatus] = useState<DaznTokenStatus | null>(null);
  const [selectedProxyId, setSelectedProxyId] = useState<number | null>(null);
  const [isSettingProxy, setIsSettingProxy] = useState(false);
  const [isLoggingOut, setIsLoggingOut] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isRetrievingDeviceId, setIsRetrievingDeviceId] = useState(false);
  const [deviceIdError, setDeviceIdError] = useState<string | null>(null);

  // New state for devices and subscriptions
  const [devices, setDevices] = useState<DaznDevice[]>([]);
  const [subscriptions, setSubscriptions] = useState<DaznSubscriptionDetail[]>([]);
  const [isLoadingDevices, setIsLoadingDevices] = useState(false);
  const [isDisconnectingDevices, setIsDisconnectingDevices] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors, isSubmitting },
  } = useForm<DaznForm>({
    resolver: zodResolver(daznSchema),
    defaultValues: {
      dazn_country: 'IT',
      dazn_language: 'it',
      dazn_timezone: 'Europe/Rome',
    },
  });

  const fetchData = async () => {
    try {
      const [accountResponse, proxiesData] = await Promise.all([
        daznService.getAccount(),
        proxyService.list(),
      ]);

      setAccount(accountResponse.account);
      setHasDefaultProxy(accountResponse.hasDefaultProxy);
      const activeProxies = proxiesData.filter(p => p.is_active);
      setProxies(activeProxies);

      // Set selected proxy to the default one
      const defaultProxy = activeProxies.find(p => p.is_default);
      if (defaultProxy) {
        setSelectedProxyId(defaultProxy.id);
      } else if (activeProxies.length > 0) {
        setSelectedProxyId(activeProxies[0].id);
      }

      if (accountResponse.account) {
        reset({
          dazn_email: accountResponse.account.dazn_email || '',
          dazn_password: accountResponse.account.dazn_password || '',
          dazn_device_id: accountResponse.account.dazn_device_id || '',
          dazn_country: accountResponse.account.dazn_country || 'IT',
          dazn_language: accountResponse.account.dazn_language || 'it',
          dazn_timezone: accountResponse.account.dazn_timezone || 'Europe/Rome',
          dazn_pin: accountResponse.account.dazn_pin || '',
          dazn_user_agent: accountResponse.account.dazn_user_agent || '',
          dazn_x_dazn_ua: accountResponse.account.dazn_x_dazn_ua || '',
          proxy_id: defaultProxy ? String(defaultProxy.id) : '',
        });

        try {
          const status = await daznService.getTokenStatus();
          setTokenStatus(status);

          // If logged in, fetch devices and subscriptions
          if (status.valid) {
            fetchDevicesAndSubscriptions();
          }
        } catch {
          setTokenStatus(null);
        }
      }
    } catch (error) {
      console.log('No DAZN account configured');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchDevicesAndSubscriptions = async () => {
    setIsLoadingDevices(true);
    try {
      const [devicesData, subscriptionsData] = await Promise.all([
        daznService.getDevices(),
        daznService.getSubscriptions(),
      ]);
      setDevices(devicesData);
      setSubscriptions(withSubscriptionData<DaznSubscriptionDetail>(subscriptionsData));
    } catch (error) {
      console.error('Failed to fetch devices/subscriptions:', error);
    } finally {
      setIsLoadingDevices(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const onSubmit = async (data: DaznForm) => {
    try {
      const payload = {
        dazn_email: data.dazn_email,
        ...(data.dazn_password && { dazn_password: data.dazn_password }),
        dazn_device_id: data.dazn_device_id || undefined,
        dazn_country: data.dazn_country,
        // Lingua forzata a 'it' lato backend (catalogo/schedule/EPG canonici IT). Non selezionabile.
        dazn_language: 'it',
        dazn_timezone: data.dazn_timezone,
        dazn_pin: data.dazn_pin || undefined,
        // Hidden identity fields: the stored values go back as they are, empty ones are not sent (server keeps them).
        ...identityPayload(data),
      };

      await daznService.updateAccount(payload);

      // Set default proxy if selected
      if (data.proxy_id) {
        const proxyId = Number(data.proxy_id);
        if (proxyId && proxyId !== selectedProxyId) {
          await proxyService.setDefault(proxyId);
        }
      }

      toast.success(t('common.saved'));
      setIsEditing(false);
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error) || t('common.error'));
    }
  };

  // Login to DAZN
  const handleLogin = async () => {
    setIsTesting(true);
    try {
      const result = await daznService.login();
      if (result.success) {
        toast.success(t('dazn.loginSuccess'));

        // Profile is now fetched synchronously during login — refresh to show it
        await fetchData();
      } else {
        toast.error(`${t('dazn.loginFailed')}: ${result.message}`);
      }
    } catch (error) {
      toast.error(getErrorMessage(error) || t('dazn.loginFailed'));
    } finally {
      setIsTesting(false);
    }
  };

  // Refresh DAZN token
  const handleRefreshToken = async () => {
    setIsRefreshing(true);
    try {
      // Call login again to refresh token (DAZN re-authenticates)
      const result = await daznService.login();
      if (result.success) {
        toast.success(t('dazn.tokenRefreshed'));
        await fetchData();
      } else {
        toast.error(`${t('dazn.tokenRefreshFailed')}: ${result.message}`);
      }
    } catch (error) {
      toast.error(getErrorMessage(error) || t('dazn.tokenRefreshFailed'));
    } finally {
      setIsRefreshing(false);
    }
  };

  const handleSetDefaultProxy = async (proxyId: number) => {
    setIsSettingProxy(true);
    try {
      await proxyService.setDefault(proxyId);
      setSelectedProxyId(proxyId);
      setHasDefaultProxy(true);
      toast.success(t('proxy.setAsDefault'));
      fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error) || t('proxy.setDefaultError'));
    } finally {
      setIsSettingProxy(false);
    }
  };

  // Logout from DAZN
  const handleLogout = async () => {
    setIsLoggingOut(true);
    try {
      await daznService.logout();
      setTokenStatus(null);
      setDevices([]);
      setSubscriptions([]);
      toast.success(t('dazn.logoutSuccess'));
      // Refresh data to clear profile
      await fetchData();
    } catch (error) {
      toast.error(getErrorMessage(error) || t('dazn.logoutError'));
    } finally {
      setIsLoggingOut(false);
    }
  };

  // Disconnect all devices
  const handleDisconnectAllDevices = async () => {
    if (!confirm(t('dazn.confirmDisconnectAll'))) {
      return;
    }

    setIsDisconnectingDevices(true);
    try {
      await daznService.disconnectAllDevices();
      toast.success(t('dazn.devicesDisconnected'));
      // Refresh devices list
      await fetchDevicesAndSubscriptions();
    } catch (error) {
      toast.error(getErrorMessage(error) || t('dazn.disconnectError'));
    } finally {
      setIsDisconnectingDevices(false);
    }
  };

  const handleRetrieveDeviceId = async () => {
    const email = getValues('dazn_email');
    const password = getValues('dazn_password');
    const country = getValues('dazn_country');
    const language = getValues('dazn_language');

    if (!email || !password) {
      toast.error(t('dazn.fillEmailPassword'));
      return;
    }

    const proxy = proxies.find(p => p.is_default) || proxies[0];
    if (!proxy) {
      toast.error(t('dazn.noProxyForDeviceId'));
      return;
    }

    setIsRetrievingDeviceId(true);
    setDeviceIdError(null);

    try {
      const result = await daznService.autoGetDeviceId({
        email,
        password,
        proxy_id: proxy.id,
        country,
        language,
      });

      setValue('dazn_device_id', result.deviceId);
      toast.success(t('dazn.deviceIdRetrieved'));
    } catch (error: unknown) {
      const errorResponse = error as { response?: { data?: { errorCode?: string; error?: string | { message?: string } } } };
      const errorCode = errorResponse?.response?.data?.errorCode;
      const rawError = errorResponse?.response?.data?.error;
      const errorMessage = typeof rawError === 'string' ? rawError : rawError?.message;

      let displayError: string;
      switch (errorCode) {
        case 'INVALID_CREDENTIALS':
          displayError = t('dazn.retrieveInvalidCredentials');
          break;
        case 'ACCOUNT_BANNED':
          displayError = t('dazn.retrieveAccountBanned');
          break;
        case 'TIMEOUT':
          displayError = t('dazn.retrieveTimeout');
          break;
        case 'NETWORK_ERROR':
          displayError = t('dazn.retrieveNetworkError');
          break;
        default:
          displayError = errorMessage || getErrorMessage(error) || t('dazn.retrieveError');
      }

      setDeviceIdError(displayError);
      toast.error(displayError);
    } finally {
      setIsRetrievingDeviceId(false);
    }
  };

  const hasActiveProxy = proxies.length > 0 || hasDefaultProxy;
  const isConfigured = account && account.dazn_email;
  const profile = account?.profile;
  const isLoggedIn = tokenStatus?.valid;

  // Check if we have DAZN profile languages (from login)
  const hasDaznLanguages = profile?.supportedLanguages && profile.supportedLanguages.length > 0;
  const daznLanguages = hasDaznLanguages ? profile.supportedLanguages : [];

  const getCountryInfo = (code: string) => DAZN_COUNTRIES.find(c => c.code === code) || { code, name: code, flag: '' };
  const getLanguageName = (code: string) => DAZN_LANGUAGES.find(l => l.code === code)?.name || code.toUpperCase();
  const getTimezoneName = (code: string) => TIMEZONES.find(tz => tz.code === code)?.name || code;

  const formatDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('it-IT');
  };

  const formatShortDate = (dateStr: string | null) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Loading State
  if (isLoading) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-dark-100">{t('dazn.title')}</h1>
        </div>
        <div className="card">
          <div className="animate-pulse space-y-4">
            {[...Array(6)].map((_, i) => (
              <div key={i} className="h-12 bg-dark-800 rounded-lg" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-dark-100">{t('dazn.title')}</h1>
          <p className="text-dark-400 mt-1">
            {isConfigured ? t('dazn.manageAccount') : t('dazn.configureAccount')}
          </p>
        </div>
        {isConfigured && !isEditing && (
          <button onClick={() => setIsEditing(true)} className="btn-secondary flex items-center gap-2">
            <Icons.Edit />
            {t('common.edit')}
          </button>
        )}
      </div>

      {/* Warning: Proxy Required */}
      {!hasActiveProxy && (
        <div className="bg-warning-50 border border-warning-100 rounded-lg p-5">
          <div className="flex items-start gap-4">
            <div className="w-10 h-10 rounded-lg bg-warning-100 flex items-center justify-center flex-shrink-0">
              <span className="text-warning-600"><Icons.Warning /></span>
            </div>
            <div>
              <h3 className="text-warning-600 font-semibold">{t('proxy.proxyRequired')}</h3>
              <p className="text-dark-400 text-sm mt-1">
                {t('dazn.proxyRequired')}
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Widevine device: each user manages only their own WVD */}
      <WvdCard />

      {/* Account Configured - View Mode */}
      {isConfigured && !isEditing ? (
        <div className="space-y-6">
          {/* Status + Subscription + Proxy Row */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {/* Connection Status */}
            <div className={`rounded-lg p-5 border ${
              isLoggedIn
                ? 'bg-success-50 border-success-100'
                : 'bg-dark-800 border-dark-700'
            }`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    isLoggedIn ? 'bg-success-100' : 'bg-dark-700'
                  }`}>
                    {isLoggedIn ? (
                      <span className="text-success-600"><Icons.Check /></span>
                    ) : (
                      <span className="text-dark-500"><Icons.X /></span>
                    )}
                  </div>
                  <div>
                    <p className={`font-semibold ${isLoggedIn ? 'text-success-600' : 'text-dark-400'}`}>
                      {isLoggedIn ? t('dazn.connected') : t('dazn.disconnected')}
                    </p>
                    {isLoggedIn && tokenStatus!.minutesRemaining > 0 ? (
                      <p className={`text-xs ${tokenStatus!.minutesRemaining < 15 ? 'text-warning-600' : 'text-dark-500'}`}>
                        {tokenStatus!.minutesRemaining < 15 ? (
                          <>{t('dazn.tokenExpiresIn', { minutes: tokenStatus!.minutesRemaining })}!</>
                        ) : (
                          <>{t('dazn.tokenValidFor', { minutes: tokenStatus!.minutesRemaining })}</>
                        )}
                      </p>
                    ) : !isLoggedIn && (
                      <p className="text-dark-500 text-xs">{t('dazn.loginToUse')}</p>
                    )}
                  </div>
                </div>
              </div>

              {/* Token Warning Banner */}
              {isLoggedIn && tokenStatus!.minutesRemaining > 0 && tokenStatus!.minutesRemaining < 15 && (
                <div className="mt-3 p-2.5 bg-warning-50 border border-warning-100 rounded-lg">
                  <p className="text-warning-600 text-xs flex items-center gap-2">
                    <Icons.Warning />
                    {t('dazn.tokenExpiringSoon')}
                  </p>
                </div>
              )}

              <div className="flex gap-2 mt-4">
                {!isLoggedIn ? (
                  /* Not Connected - Show Login button */
                  <button
                    onClick={handleLogin}
                    disabled={isTesting || !hasActiveProxy}
                    className="flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold bg-primary-600 text-white hover:bg-primary-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                  >
                    {isTesting ? (
                      <>
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        {t('dazn.loggingIn')}
                      </>
                    ) : (
                      <>
                        <Icons.Zap />
                        Login
                      </>
                    )}
                  </button>
                ) : (
                  /* Connected - Show Refresh Token and Logout buttons */
                  <>
                    <button
                      onClick={handleRefreshToken}
                      disabled={isRefreshing}
                      className="flex-1 px-4 py-2.5 rounded-lg text-sm font-semibold bg-success-50 text-success-600 hover:bg-success-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                    >
                      {isRefreshing ? (
                        <>
                          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                          {t('dazn.refreshing')}
                        </>
                      ) : (
                        <>
                          <Icons.Zap />
                          {t('dazn.refreshToken')}
                        </>
                      )}
                    </button>
                    <button
                      onClick={handleLogout}
                      disabled={isLoggingOut}
                      className="px-4 py-2.5 rounded-lg text-sm font-semibold bg-error-50 text-error-600 hover:bg-error-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2"
                    >
                      <Icons.Logout />
                      {isLoggingOut ? '...' : t('dazn.logout')}
                    </button>
                  </>
                )}
              </div>
            </div>

            {/* Subscription Status - only show when logged in with subscriptions data */}
            {isLoggedIn && subscriptions.length > 0 && (
              <div className={`rounded-lg p-5 border ${
                subscriptions[0].status === 'Active'
                  ? 'bg-primary-50 border-primary-100'
                  : 'bg-dark-800 border-dark-700'
              }`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    subscriptions[0].status === 'Active' ? 'bg-primary-100' : 'bg-dark-700'
                  }`}>
                    <span className={subscriptions[0].status === 'Active' ? 'text-primary-600' : 'text-dark-500'}>
                      <Icons.Crown />
                    </span>
                  </div>
                  <div>
                    <p className={`font-semibold ${subscriptions[0].status === 'Active' ? 'text-primary-600' : 'text-dark-400'}`}>
                      {subscriptions[0].status === 'Active' ? t('dazn.subscriptionActive') : subscriptions[0].status}
                    </p>
                    {subscriptions[0].currentPlan && (
                      <p className="text-dark-500 text-xs">{subscriptions[0].currentPlan}</p>
                    )}
                  </div>
                </div>

                {/* Subscription details */}
                <div className="space-y-2 text-sm">
                  {subscriptions[0].activePass && (
                    <div className="flex items-center gap-2 text-dark-400">
                      <Icons.CreditCard />
                      <span>{subscriptions[0].activePass.price} {subscriptions[0].activePass.currency}/{subscriptions[0].activePass.period}</span>
                    </div>
                  )}
                  {subscriptions[0].nextPaymentDate && (
                    <div className="flex items-center gap-2 text-dark-400">
                      <Icons.Calendar />
                      <span>{t('dazn.nextPayment')}: {formatShortDate(subscriptions[0].nextPaymentDate)}</span>
                    </div>
                  )}
                  {subscriptions[0].termEndDate && (
                    <div className="flex items-center gap-2 text-dark-400">
                      <Icons.Clock />
                      <span>{t('dazn.expires')}: {formatShortDate(subscriptions[0].termEndDate)}</span>
                    </div>
                  )}
                  {subscriptions[0].paymentMethod && (
                    <div className="flex items-center gap-2 text-dark-500 text-xs">
                      <span>{subscriptions[0].paymentMethod.type}</span>
                      {subscriptions[0].paymentMethod.details.payerEmail && (
                        <span>({subscriptions[0].paymentMethod.details.payerEmail})</span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Fallback to profile subscription if no detailed subscription data */}
            {isLoggedIn && subscriptions.length === 0 && profile?.subscription && (
              <div className={`rounded-lg p-5 border ${
                profile.subscription.status?.includes('Active')
                  ? 'bg-primary-50 border-primary-100'
                  : 'bg-dark-800 border-dark-700'
              }`}>
                <div className="flex items-center gap-3 mb-3">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                    profile.subscription.status?.includes('Active') ? 'bg-primary-100' : 'bg-dark-700'
                  }`}>
                    <span className={profile.subscription.status?.includes('Active') ? 'text-primary-600' : 'text-dark-500'}>
                      <Icons.Crown />
                    </span>
                  </div>
                  <div>
                    <p className={`font-semibold ${profile.subscription.status?.includes('Active') ? 'text-primary-600' : 'text-dark-400'}`}>
                      {profile.subscription.status === 'ActivePaid' ? t('dazn.subscriptionActive') : (profile.subscription.status || t('dazn.noSubscription'))}
                    </p>
                    {profile.subscription.plan && (
                      <p className="text-dark-500 text-xs">{t('dazn.services')}: {profile.subscription.plan}</p>
                    )}
                  </div>
                </div>
                {profile.subscription.expiresAt && (
                  <p className="text-dark-500 text-xs">
                    {profile.subscription.expiresAt === '9999-12-31' ? t('dazn.noExpiration') : `${t('dazn.expires')}: ${formatDate(profile.subscription.expiresAt)}`}
                  </p>
                )}
              </div>
            )}

            {/* Logged in but DAZN gave no subscription data at all */}
            {isLoggedIn && !isLoadingDevices && subscriptions.length === 0 && !profile?.subscription && (
              <div className="rounded-lg p-5 border bg-dark-800 border-dark-700">
                <p className="text-dark-400 text-sm">{t('dazn.subscriptionDataUnavailable')}</p>
              </div>
            )}

            {/* Proxy Selection */}
            <div className={`rounded-lg p-5 border ${
              hasActiveProxy
                ? 'bg-primary-50 border-primary-100'
                : 'bg-dark-800 border-dark-700'
            }`}>
              <div className="flex items-center gap-3 mb-3">
                <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${
                  hasActiveProxy ? 'bg-primary-100' : 'bg-dark-700'
                }`}>
                  <span className={hasActiveProxy ? 'text-primary-600' : 'text-dark-500'}>
                    <Icons.Shield />
                  </span>
                </div>
                <div>
                  <p className={`font-semibold ${hasActiveProxy ? 'text-primary-600' : 'text-dark-500'}`}>
                    {hasActiveProxy ? t('dazn.proxyActive') : t('dazn.noProxy')}
                  </p>
                  <p className="text-dark-500 text-xs">
                    {proxies.length > 0 ? t('dazn.proxiesAvailable', { count: proxies.length }) : t('dazn.configureProxy')}
                  </p>
                </div>
              </div>
              {proxies.length > 0 ? (
                <select
                  value={selectedProxyId || ''}
                  onChange={(e) => handleSetDefaultProxy(Number(e.target.value))}
                  disabled={isSettingProxy}
                  className="w-full mt-2 px-3 py-2.5 bg-dark-800 border border-dark-700 rounded-lg text-dark-200 text-sm focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary-500 disabled:opacity-50"
                >
                  {proxies.map(proxy => (
                    <option key={proxy.id} value={proxy.id}>
                      {proxy.name || `${proxy.proxy_host}:${proxy.proxy_port}`}
                      {proxy.is_default ? ` (${t('common.default')})` : ''}
                    </option>
                  ))}
                </select>
              ) : (
                <a href="/user/proxies" className="block w-full mt-2 px-4 py-2.5 text-center rounded-lg text-sm font-semibold bg-primary-50 text-primary-600 hover:bg-primary-100 transition-all">
                  {t('dazn.configureProxy')}
                </a>
              )}
            </div>
          </div>


          {/* Profile Card (if logged in) */}
          {isLoggedIn && profile && (
            <div className="card">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-dark-700">
                <div className="w-14 h-14 rounded-lg bg-primary-600 flex items-center justify-center shadow-card">
                  <span className="text-white text-2xl font-bold">
                    {profile.firstName?.charAt(0) || account.dazn_email?.charAt(0).toUpperCase() || 'D'}
                  </span>
                </div>
                <div>
                  <h2 className="text-xl font-bold text-dark-100">
                    {profile.firstName && profile.lastName
                      ? `${profile.firstName} ${profile.lastName}`
                      : t('dazn.profile')}
                  </h2>
                  <p className="text-dark-400 text-sm">{account.dazn_email}</p>
                </div>
                {profile.lastLoginAt && (
                  <div className="ml-auto text-right">
                    <p className="text-dark-500 text-xs">{t('dazn.lastLogin')}</p>
                    <p className="text-dark-400 text-sm">{formatDate(profile.lastLoginAt)}</p>
                  </div>
                )}
              </div>

              {/* Entitlements - Servizi Attivi */}
              {profile.entitlements && profile.entitlements.length > 0 && (
                <div className="mb-6">
                  <p className="text-dark-500 text-xs uppercase tracking-wider mb-3">{t('dazn.activeServices')}</p>
                  <div className="flex flex-wrap gap-2">
                    {profile.entitlements.map((ent, idx) => {
                      // Parse "ServiceName: Status" format
                      const [serviceName, status] = ent.split(': ');
                      const isActive = status === 'ActivePaid';
                      const isPartial = status === 'Partial';
                      return (
                        <span
                          key={idx}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium flex items-center gap-1.5 ${
                            isActive
                              ? 'bg-success-50 text-success-600 border border-success-100'
                              : isPartial
                                ? 'bg-warning-50 text-warning-600 border border-warning-100'
                                : 'bg-dark-800 text-dark-400 border border-dark-700'
                          }`}
                        >
                          {isActive && <Icons.Check />}
                          {serviceName}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Supported Languages from DAZN */}
              {hasDaznLanguages && (
                <div className="mb-6">
                  <p className="text-dark-500 text-xs uppercase tracking-wider mb-3">{t('dazn.supportedLanguages')} ({daznLanguages.length})</p>
                  <div className="flex flex-wrap gap-2">
                    {daznLanguages.map((lang, idx) => (
                      <span key={idx} className="px-3 py-1.5 rounded-lg text-xs font-medium bg-primary-50 text-primary-600 border border-primary-100">
                        {lang.toUpperCase()} - {getLanguageName(lang)}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Account Details Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                <InfoItem icon={<Icons.Mail />} label={t('dazn.email')} value={account.dazn_email || '-'} />
                <InfoItem icon={<Icons.Globe />} label={t('dazn.country')} value={`${getCountryInfo(account.dazn_country).flag} ${getCountryInfo(account.dazn_country).name}`} />
                <InfoItem icon={<Icons.User />} label={t('dazn.language')} value={getLanguageName(account.dazn_language)} />
                <InfoItem icon={<Icons.Clock />} label={t('dazn.timezone')} value={getTimezoneName(account.dazn_timezone)} />
                <InfoItem icon={<Icons.Device />} label={t('dazn.deviceId')} value={account.dazn_device_id || '-'} mono />
                <InfoItem icon={<Icons.Key />} label={t('dazn.pin')} value={account.dazn_pin || t('dazn.notConfigured')} />
              </div>

            </div>
          )}

          {/* Devices Section (only when logged in) */}
          {isLoggedIn && (
            <div className="card">
              <div className="flex items-center justify-between mb-6 pb-4 border-b border-dark-700">
                <div className="flex items-center gap-3">
                  <div className="w-12 h-12 rounded-lg bg-primary-50 flex items-center justify-center">
                    <span className="text-primary-600"><Icons.Monitor /></span>
                  </div>
                  <div>
                    <h2 className="text-lg font-semibold text-dark-100">{t('dazn.connectedDevices')}</h2>
                    <p className="text-dark-500 text-sm">
                      {devices.length > 0
                        ? t('dazn.devicesCount', { count: devices.length })
                        : t('dazn.noDevices')}
                    </p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <button
                    onClick={fetchDevicesAndSubscriptions}
                    disabled={isLoadingDevices}
                    className="p-2 rounded-lg text-dark-400 hover:text-dark-200 hover:bg-dark-800 transition-all disabled:opacity-50"
                    title={t('common.refresh')}
                  >
                    <Icons.Refresh />
                  </button>
                  {devices.length > 0 && (
                    <button
                      onClick={handleDisconnectAllDevices}
                      disabled={isDisconnectingDevices}
                      className="px-4 py-2 rounded-lg text-sm font-semibold bg-error-50 text-error-600 hover:bg-error-100 transition-all disabled:opacity-50 flex items-center gap-2"
                    >
                      {isDisconnectingDevices ? (
                        <>
                          <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                          </svg>
                          {t('common.loading')}
                        </>
                      ) : (
                        <>
                          <Icons.Trash />
                          {t('dazn.disconnectAll')}
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>

              {isLoadingDevices ? (
                <div className="animate-pulse space-y-3">
                  {[...Array(2)].map((_, i) => (
                    <div key={i} className="h-20 bg-dark-800 rounded-lg" />
                  ))}
                </div>
              ) : devices.length > 0 ? (
                <div className="space-y-3">
                  {devices.map((device) => (
                    <div key={device.id} className="p-4 bg-dark-800 rounded-lg border border-dark-700">
                      <div className="flex items-start justify-between">
                        <div className="flex items-start gap-3">
                          <div className="w-10 h-10 rounded-lg bg-dark-700 flex items-center justify-center flex-shrink-0">
                            <span className="text-dark-400"><Icons.Monitor /></span>
                          </div>
                          <div>
                            <p className="font-medium text-dark-200">{device.name} {device.model}</p>
                            <p className="text-dark-500 text-sm">{device.os} {device.osVersion}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-dark-500">
                              <span className="flex items-center gap-1">
                                <Icons.MapPin />
                                {device.city}, {device.countrycode}
                              </span>
                              <span>{device.ipAddress}</span>
                            </div>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className={`inline-flex px-2 py-1 rounded text-xs font-medium ${
                            device.status === 'Active'
                              ? 'bg-success-50 text-success-600'
                              : 'bg-dark-700 text-dark-400'
                          }`}>
                            {device.status}
                          </span>
                          {device.playbackTimestamp && (
                            <p className="text-dark-500 text-xs mt-1">
                              {t('dazn.lastActivity')}: {formatDate(device.playbackTimestamp)}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-dark-400">
                  <Icons.Monitor />
                  <p className="mt-2">{t('dazn.noDevices')}</p>
                </div>
              )}
            </div>
          )}

          {/* Account Details Card (no profile yet - not loading, not logged in) */}
          {!isLoggedIn && (
            <div className="card">
              <div className="flex items-center gap-3 mb-6 pb-4 border-b border-dark-700">
                <div className="w-12 h-12 rounded-lg bg-primary-50 flex items-center justify-center">
                  <span className="text-primary-600 text-xl font-bold">
                    {account.dazn_email?.charAt(0).toUpperCase() || 'D'}
                  </span>
                </div>
                <div>
                  <h2 className="text-lg font-semibold text-dark-100">{t('dazn.title')}</h2>
                  <p className="text-dark-400 text-sm">{account.dazn_email}</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
                <InfoItem icon={<Icons.Mail />} label={t('dazn.email')} value={account.dazn_email || '-'} />
                <InfoItem icon={<Icons.Globe />} label={t('dazn.country')} value={`${getCountryInfo(account.dazn_country).flag} ${getCountryInfo(account.dazn_country).name}`} />
                <InfoItem icon={<Icons.User />} label={t('dazn.language')} value={getLanguageName(account.dazn_language)} />
                <InfoItem icon={<Icons.Clock />} label={t('dazn.timezone')} value={getTimezoneName(account.dazn_timezone)} />
                <InfoItem icon={<Icons.Device />} label={t('dazn.deviceId')} value={account.dazn_device_id || '-'} mono />
                <InfoItem icon={<Icons.Key />} label={t('dazn.pin')} value={account.dazn_pin || t('dazn.notConfigured')} />
              </div>

              <div className="mt-6 p-4 bg-dark-800 rounded-lg border border-dark-700">
                <p className="text-dark-400 text-sm">
                  {t('dazn.loginPrompt')}
                </p>
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Form Mode */
        <div className="card">
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-dark-700">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-lg bg-primary-50 flex items-center justify-center">
                <span className="text-primary-600"><Icons.User /></span>
              </div>
              <div>
                <h2 className="text-lg font-semibold text-dark-100">
                  {isEditing ? t('dazn.editAccount') : t('dazn.configureAccountTitle')}
                </h2>
                <p className="text-dark-500 text-sm">
                  {isEditing ? t('dazn.editCredentials') : t('dazn.enterCredentials')}
                </p>
              </div>
            </div>
            {isEditing && (
              <button
                onClick={() => { setIsEditing(false); fetchData(); }}
                className="p-2 rounded-lg text-dark-400 hover:text-dark-200 hover:bg-dark-800 transition-all"
              >
                <Icons.X />
              </button>
            )}
          </div>

          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="label"><Icons.Mail /> {t('dazn.email')}</label>
                <input type="email" className={errors.dazn_email ? 'input-error' : 'input'} placeholder="email@dazn.com" {...register('dazn_email')} />
                {errors.dazn_email && <p className="mt-2 text-sm text-error-600">{t(errors.dazn_email.message || '')}</p>}
              </div>
              <div>
                <label className="label">
                  <Icons.Key /> {t('dazn.password')}
                  {isEditing && <span className="text-dark-500 font-normal text-xs ml-2">({t('common.leaveBlankToKeep')})</span>}
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="input pr-10"
                    placeholder="••••••••"
                    {...register('dazn_password')}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-dark-400 hover:text-dark-200 transition-colors"
                  >
                    {showPassword ? <Icons.EyeOff /> : <Icons.Eye />}
                  </button>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="label"><Icons.Device /> {t('dazn.deviceId')}</label>
                <div className="flex gap-2">
                  <input type="text" className="input font-mono flex-1" placeholder="0065f42b80" {...register('dazn_device_id')} />
                  <button
                    type="button"
                    onClick={handleRetrieveDeviceId}
                    disabled={isRetrievingDeviceId}
                    className="px-4 py-2.5 rounded-lg text-sm font-semibold bg-primary-50 text-primary-600 hover:bg-primary-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 whitespace-nowrap"
                  >
                    {isRetrievingDeviceId ? (
                      <>
                        <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                        </svg>
                        {t('dazn.retrieving')}
                      </>
                    ) : (
                      <>
                        <Icons.Refresh />
                        {t('dazn.retrieveDeviceId')}
                      </>
                    )}
                  </button>
                </div>
                <p className="text-dark-500 text-xs mt-2">{t('dazn.deviceIdHelp')}</p>
                {deviceIdError && (
                  <p className="mt-1 text-sm text-error-600">{deviceIdError}</p>
                )}
              </div>
              <div>
                <label className="label"><Icons.Shield /> Proxy</label>
                {proxies.length > 0 ? (
                  <select className="input" {...register('proxy_id')}>
                    <option value="">{t('dazn.selectProxy')}</option>
                    {proxies.map(proxy => (
                      <option key={proxy.id} value={proxy.id}>
                        {proxy.name || `${proxy.proxy_host}:${proxy.proxy_port}`}
                        {proxy.is_default ? ` (${t('common.default')})` : ''}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="input bg-dark-800 text-dark-500 flex items-center justify-between">
                    <span>{t('dazn.noProxyConfigured')}</span>
                    <a href="/user/proxies" className="text-primary-600 text-sm hover:underline">{t('common.configure')}</a>
                  </div>
                )}
                <p className="text-dark-500 text-xs mt-2">{t('dazn.proxyHelp')}</p>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="label"><Icons.Globe /> {t('dazn.country')}</label>
                <select className="input" {...register('dazn_country')}>
                  {DAZN_COUNTRIES.map(c => <option key={c.code} value={c.code}>{c.flag} {c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="label"><Icons.Clock /> {t('dazn.timezone')}</label>
                <select className="input" {...register('dazn_timezone')}>
                  {TIMEZONES.map(tz => <option key={tz.code} value={tz.code}>{tz.name}</option>)}
                </select>
              </div>
            </div>

            <div className="max-w-xs">
              <label className="label"><Icons.Shield /> {t('dazn.pin')}</label>
              <input type="text" className="input" maxLength={4} placeholder="1234" {...register('dazn_pin')} />
              <p className="text-dark-500 text-xs mt-2">{t('dazn.pinHelp')}</p>
            </div>


            <div className="flex justify-end gap-3 pt-4 border-t border-dark-700">
              {isEditing && (
                <button type="button" onClick={() => { setIsEditing(false); fetchData(); }} className="btn-secondary">
                  {t('common.cancel')}
                </button>
              )}
              <button type="button" onClick={handleLogin} disabled={isTesting || !hasActiveProxy} className="btn-secondary flex items-center gap-2">
                <Icons.Zap />
                {isTesting ? `${t('dazn.login')}...` : t('dazn.testLogin')}
              </button>
              <button type="submit" disabled={isSubmitting} className="btn-primary flex items-center gap-2">
                {isSubmitting ? (
                  <>
                    <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                    {t('common.saving')}
                  </>
                ) : (
                  <>
                    <Icons.Check />
                    {t('common.save')}
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}

// Helper component for info items
function InfoItem({ icon, label, value, mono }: { icon: React.ReactNode; label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex items-start gap-3">
      <div className="w-10 h-10 rounded-lg bg-dark-800 flex items-center justify-center flex-shrink-0 border border-dark-700">
        <span className="text-dark-400">{icon}</span>
      </div>
      <div className="min-w-0">
        <p className="text-dark-500 text-xs uppercase tracking-wider mb-1">{label}</p>
        <p className={`text-dark-200 font-medium ${mono ? 'font-mono text-sm' : ''} truncate`}>{value}</p>
      </div>
    </div>
  );
}
